/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    baseStyle: {
        container: {
            minWidth: 'xs',
            width: 'full',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            display: {base: 'none', lg: 'flex'}
        },
        stackContainer: {
            direction: 'row',
            spacing: 0,
            whiteSpace: 'nowrap',
            flexWrap: 'wrap'
        },
        spinnerContainer: {
            p: 2
        },
        popoverContainer: {
            transform: 'unset',
            paddingTop: 4,
            paddingRight: 6,
            paddingBottom: 8,
            paddingLeft: 6,
            maxWidth: 'container.xxxl',
            // The popover renders inside the nav Stack (no portal) and would otherwise inherit
            // `whiteSpace: nowrap`, making long labels (e.g. fr-CA) overflow into adjacent columns
            whiteSpace: 'normal'
        },
        popoverContainerWithImages: {
            display: 'flex',
            overflow: 'hidden'
        },
        imageBlockContainer: {
            display: 'flex',
            gap: 2,
            flexWrap: 'no-wrap',
            justifyContent: 'end',
            flex: '1'
        },
        popoverContent: {
            marginLeft: 'auto',
            marginRight: 'auto',
            border: 0,
            minWidth: '100%',
            top: -2,
            position: 'absolute'
        },
        popoverBody: {
            padding: 0,
            boxShadow: 'xl',
            zIndex: 1,
            maxHeight: 'calc(100vh - 80px)',
            overflow: 'auto',
            boxShadow: 'none',
            border: '0',
            borderRadius: '0'
        },
        popoverOverlay: {
            position: 'fixed',
            top: '80px',
            left: '0',
            width: '100vw',
            height: '100vh',
            bg: 'webTrasparency.neutralTransparencyBlack',
            zIndex: '-1'
        },
        listMenuTriggerContainer: {
            display: 'flex',
            alignItems: 'center',
            /* Anchors the keyboard submenu toggle, which is positioned out of
             * flow so its footprint does not pad the nav item. */
            position: 'relative'
        },
        listMenuTriggerLink: {
            display: 'block',
            textStyle: 'labelXs',
            color: 'webTypo.textPrimary',
            paddingLeft: 1.5,
            paddingRight: 1.5,
            paddingTop: 3.5,
            paddingBottom: 2,
            marginBottom: 1.5,
            letterSpacing: 0,
            whiteSpace: 'nowrap',
            position: 'relative',
            _hover: {
                textDecoration: 'none'
            },
            sx: {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: 1.5,
                    left: 1,
                    width: 'calc(100% - 8px)',
                    height: '1px',
                    backgroundColor: 'currentColor',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left center',
                    zIndex: 1001
                }
            }
        },
        listMenuTriggerLinkActive: {
            textDecoration: 'none',
            sx: {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: 1.5,
                    left: 1,
                    width: 'calc(100% - 8px)',
                    height: '1px',
                    backgroundColor: 'currentColor',
                    transform: 'scaleX(1)',
                    transformOrigin: 'left center',
                    transition: 'transform 0.3s ease',
                    zIndex: 1001
                }
            }
        },
        level1Label: {
            textStyle: 'badge',
            color: 'webTypo.textTertiary',
            marginBottom: '22px',
        },
        linkSeparator: {
            _after: {
                content: '"-"',
                display: 'block',
                marginTop: 5,
                marginBottom: 5,
                textStyle: 'bodySmMedium'
            }
        },
        megaMenuGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 3
        },
        megaMenuGridColumn: {
            gridColumn: 'span 2',
            minWidth: 0,
            overflowWrap: 'break-word',
            // CMS CTAs render as Chakra Buttons styled "Mega Menu Link", which force
            // `nowrap` (needed for the top-level nav). Inside a column, let them wrap.
            sx: {
                '& .chakra-button': {
                    whiteSpace: 'normal',
                    maxWidth: '100%',
                    textAlign: 'left'
                }
            }
        },
        spacerLabel: {
            visibility: 'hidden'
        }
    },
    parts: [
        'container',
        'stackContainer',
        'popoverContent',
        'popoverContainer',
        'listMenuTriggerContainer',
        'listMenuTriggerLink',
        'listMenuTriggerLinkActive',
        'listMenuTriggerIcon'
    ]
}
